'use client'
import { faBars, faChevronDown, faX } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  Dialog,
  DialogPanel,
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from '@headlessui/react'
import { GeneralInformation } from '@/payload-types'
import { HttpTypes } from '@medusajs/types'
import { useState } from 'react'
import { CMSLink } from '../../components/CMSLink'
import Link from 'next/link'
import { signout } from '@onclicks/sharedmedusa/medusaData/customer'
import { UserMenu } from './UserMenu'
import { Button } from '../ui/Button'
import { Logo } from '../Logo'
import { useNumberItemsCart } from '@/providers/NumberItemsCartProvider'

type MobileNavProps = GeneralInformation & {
  customer: HttpTypes.StoreCustomer | null
}

export const MobileNav: React.FC<MobileNavProps> = (props) => {
  const { navigationItems, customer, logo } = props || []
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const { setQuantity } = useNumberItemsCart()

  function handleClick(e: React.MouseEvent<HTMLDivElement>) {
    const target = e.target as HTMLElement
    if (target.closest('a')) {
      setMobileMenuOpen(false)
    }
  }

  return (
    <>
      <header
        className={`
        backdrop-blur-md bg-white/70
        fixed top-0 right-5 mt-3
        border border-white/40 shadow-lg
        rounded-full lg:hidden
        ${mobileMenuOpen ? 'hidden' : ''}
        `}
      >
        <nav aria-label="Global" className="mx-auto flex items-center justify-end gap-3 p-2 px-3">
          <div className="flex lg:hidden items-center gap-3">
            <UserMenu customer={customer} iconSize={22} />
            <div aria-hidden="true" className="h-6 w-px bg-primary/20" />
            <button
              type="button"
              onClick={() => setMobileMenuOpen(true)}
              className="inline-flex items-center justify-center rounded-full p-1 text-primary"
            >
              <span className="sr-only">Open main menu</span>
              <FontAwesomeIcon icon={faBars} aria-hidden="true" className="size-6" />
            </button>
          </div>
          <Dialog open={mobileMenuOpen} onClose={setMobileMenuOpen} className="lg:hidden z-1001">
            <div className="fixed inset-0 z-1001" />
            <DialogPanel
              onClickCapture={(e) => handleClick(e)}
              className="fixed inset-y-0 right-0 z-1002 w-full overflow-y-auto bg-linear-to-b from-primary to-secondary px-6 pt-6 sm:max-w-sm sm:ring-1 sm:ring-gray-900/10"
            >
              <button
                type="button"
                onClick={() => setMobileMenuOpen(false)}
                className="w-full justify-end flex rounded-md px-2.5 text-gray-700"
              >
                <span className="sr-only">Close menu</span>
                <FontAwesomeIcon icon={faX} aria-hidden="true" className="size-6 text-white" />
              </button>
              <div className="mt-6 flex flex-col min-h-[85vh]">
                <div className="-my-6 divide-y divide-gray-500/10">
                  <div className="space-y-2 pt-6 flex flex-col">
                    {customer && (
                      <p className="text-white font-semibold mb-5">
                        Bonjour {customer.first_name} !
                      </p>
                    )}
                    {navigationItems &&
                      navigationItems.map(({ id, links, linkWithMedia }) => {
                        if (links && links.length > 0) {
                          return (
                            <Disclosure key={id}>
                              <DisclosureButton className="group flex w-full items-center justify-between rounded-lg py-2 text-base/7 font-semibold text-white">
                                {linkWithMedia && linkWithMedia?.label}
                                <FontAwesomeIcon
                                  icon={faChevronDown}
                                  aria-hidden="true"
                                  className="size-5 flex-none group-data-open:rotate-180 pr-3"
                                />
                              </DisclosureButton>
                              <DisclosurePanel className="mb-2">
                                {links.map(({ id: link_id, link }) => {
                                  if (!link) {
                                    return null
                                  } else {
                                    return (
                                      <DisclosureButton
                                        key={link_id}
                                        className="block rounded-lg py-2 text-sm/7 font-semibold text-white"
                                      >
                                        <CMSLink
                                          className="py-2 pl-6 font-semibold text-white"
                                          {...link}
                                          appearance="inline"
                                        />
                                      </DisclosureButton>
                                    )
                                  }
                                })}
                              </DisclosurePanel>
                            </Disclosure>
                          )
                        } else if (linkWithMedia && !linkWithMedia.isEshopLink) {
                          return (
                            <CMSLink
                              key={id}
                              className="py-2 font-semibold text-white"
                              {...linkWithMedia}
                              appearance="inline"
                            />
                          )
                        }
                        return null
                      })}
                  </div>

                  <Link className="flex items-center w-full" href="/panier">
                    <span className="py-2 pb-4 font-semibold text-white">Mon panier</span>
                  </Link>

                  <div className="py-4 border-t border-secondary flex flex-col items-start gap-4">
                    {customer ? (
                      <>
                        <button
                          type="button"
                          onClick={() => {
                            setMobileMenuOpen(false)
                            setQuantity(0)
                            signout()
                          }}
                          className="text-white font-semibold"
                        >
                          Se déconnecter
                        </button>
                      </>
                    ) : (
                      <Button
                        asChild
                        variant="gradientOrangeToPink"
                        className="rounded-full px-6 py-3 text-base font-semibold"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <Link href="/account">Se connecter</Link>
                      </Button>
                    )}
                  </div>
                </div>
                {logo && (
                  <Logo logo={logo} className="flex justify-center mt-auto" imgClassName="w-30" />
                )}
              </div>
            </DialogPanel>
          </Dialog>
        </nav>
      </header>
    </>
  )
}
